import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../../contexts/AuthContext/AuthContext'
import { useModal } from '../../../contexts/ModalContext/ModalContext'
import { auth } from '../../../firebase/config/config'
import { Map } from '../../../types'
import { CreateMapData } from './types'
import { MapCard } from '../MapCard/MapCard'
import { CreateMapModal } from '../CreateMapModal/CreateMapModal'
import { UsageWidget } from '../../billing/UsageWidget'
import {
  DashboardContainer,
  Header,
  Title,
  UserInfo,
  Button,
  ContentSection,
  SectionHeader,
  SectionTitle,
  MapsGrid,
  EmptyState,
  EmptyStateTitle,
  LoadingContainer,
  NavLinks,
  NavLink,
} from './styled'

const Dashboard: React.FC = () => {
  const { user, signOut } = useAuth()
  const { showError } = useModal()
  const navigate = useNavigate()
  const [maps, setMaps] = useState<Map[]>([])
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Load maps from backend
    const fetchMaps = async () => {
      if (!user?.uid) {
        setLoading(false)
        return
      }

      try {
        const idToken = await auth.currentUser?.getIdToken()
        if (!idToken) {
          setLoading(false)
          return
        }

        const response = await fetch(
          `${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/maps`,
          {
            headers: {
              Authorization: `Bearer ${idToken}`,
            },
          },
        )

        const data = await response.json()
        if (data.success) {
          setMaps(data.data || [])
        } else {
          console.error('Failed to fetch maps:', data.error)
        }
      } catch (error) {
        console.error('Error fetching maps:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchMaps()
  }, [user])

  const handleCreateMap = async (mapData: CreateMapData) => {
    if (!user) return

    try {
      const idToken = await auth.currentUser?.getIdToken()
      if (!idToken) {
        showError('Authentication error. Please sign in again.')
        return
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/maps`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({
            name: mapData.name,
            description: mapData.description,
          }),
        },
      )

      const data = await response.json()

      if (data.success) {
        const newMap = data.data
        // Add to local state
        setMaps([newMap, ...maps])
        // Navigate to the new map's editor
        navigate(`/editor/${newMap.id}`)
      } else {
        showError(data.error || 'Failed to create map')
      }
    } catch (error) {
      console.error('Error creating map:', error)
      showError('Failed to create map. Please try again.')
    }
  }

  const handleMapClick = (map: Map) => {
    navigate(`/editor/${map.id}`)
  }

  if (loading) {
    return <LoadingContainer>Loading your maps...</LoadingContainer>
  }

  return (
    <DashboardContainer>
      <Header>
        <Title>QuickCasa Waypoint</Title>
        <NavLinks>
          <NavLink onClick={() => navigate('/billing')}>Billing</NavLink>
          <NavLink href="/pricing">Pricing</NavLink>
          <NavLink href="/privacy">Privacy Policy</NavLink>
          <NavLink href="/terms">Terms of Use</NavLink>
          <NavLink
            href="https://quickcasa.ai"
            target="_blank"
            rel="noopener noreferrer">
            QuickCasa.ai ↗
          </NavLink>
        </NavLinks>
        <UserInfo>
          <span>Welcome, {user?.displayName || user?.email}!</span>
          <Button $variant="danger" onClick={signOut}>
            Sign Out
          </Button>
        </UserInfo>
      </Header>

      <UsageWidget />

      <ContentSection>
        <SectionHeader>
          <SectionTitle>Your Maps</SectionTitle>
          <Button $variant="primary" onClick={() => setShowCreateModal(true)}>
            + Create New Map
          </Button>
        </SectionHeader>

        {maps.length === 0 ? (
          <EmptyState>
            <EmptyStateTitle>No maps yet</EmptyStateTitle>
            <p>Create your first indoor map to get started!</p>
            <Button
              $variant="primary"
              onClick={() => setShowCreateModal(true)}
              style={{ marginTop: '1rem' }}>
              Create Your First Map
            </Button>
          </EmptyState>
        ) : (
          <MapsGrid>
            {maps.map(map => (
              <MapCard key={map.id} map={map} onClick={handleMapClick} />
            ))}
          </MapsGrid>
        )}
      </ContentSection>

      <CreateMapModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateMap}
      />
    </DashboardContainer>
  )
}

export { Dashboard }
